import React, { Component } from 'react';
import api from 'api';
import {
  Dropdown, Segment, Icon, Button, Label,
} from 'semantic-ui-react';
import styled, { withTheme } from 'styled-components';
import * as d3 from 'd3';
import util from 'utils/utils';

import { HandledIdeaPreview as IdeaPreview } from 'components/lib/Ideas';

const StyledChart = styled.div`
  display: block;
  height: 40vh;
  .bubbles {
    stroke-width: 2px;
    stroke: white;
    &:hover {
      stroke: black;
    }
  }
`;

const filters = [
  { key: 'All', text: 'All Ideas', value: 'All' },
  { key: 'Positive', text: 'Positive Ideas', value: 'Positive' },
  { key: 'Submitted', text: 'Submitted Ideas', value: 'Submitted' },
  { key: 'Created', text: 'Non-Submitted Ideas', value: 'Created' },
];

class IdeaChart extends Component {
  constructor(props) {
    super(props);
    this.state = {
      redirect: null, ideas: null, previewId: null, filter: 'All', options: [], xAxis: { title: 'Likes', id: 'voteCount' }, yAxis: { title: 'Comments', id: 'commentCount' }, total: 0,
    };
    this.chartRef = React.createRef(null);
  }

  componentDidMount() {
    this.getOptions();
  }

  getOptions() {
    api.challenges.getOptions(this.props.challenge._id, (data) => {
      this.setState({ options: data.options }, this.getData());
    });
  }

  getData() {
    api.challenges.getChartData(this.props.challenge._id, { filter: this.state.filter }, (data) => {
      this.setState({ ideas: data.ideas, total: data.total_ideas }, this.getChart(data.ideas, this.state.xAxis, this.state.yAxis));
    });
  }

  getChart(ideas, xAxis, yAxis) {
    const { theme } = this.props;
    d3.select(this.chartRef.current).selectAll('svg').remove();
    d3.select(this.chartRef.current).selectAll('div').remove();

    const parentelement = d3.select(this.chartRef.current).node();
    if (!parentelement) return;
    const margin = {
      top: 40, right: 40, bottom: 70, left: 50,
    };
    const width = parentelement.getBoundingClientRect().width - margin.left - margin.right;
    const height = parentelement.getBoundingClientRect().height - margin.top - margin.bottom;

    const svg = d3.select(this.chartRef.current)
      .append('svg')
      .style('position', 'relative')
      .attr('width', width + margin.left + margin.right)
      .attr('height', height + margin.top + margin.bottom)
      .append('g')
      .attr('transform',
        `translate(${margin.left},${margin.top})`);

    let xMin = 'minslider' in xAxis ? xAxis.minslider : 0;
    let xMax = 'maxslider' in xAxis ? xAxis.maxslider : 10;
    let yMin = 'minslider' in yAxis ? yAxis.minslider : 0;
    let yMax = 'maxslider' in yAxis ? yAxis.maxslider : 10;

    if (xAxis.id === 'voteCount') {
      xMin = 0;
      xMax = 1 + Math.max(ideas.map(idea => idea.templated.voteCount.value));
    }
    if (xAxis.id === 'commentCount') {
      xMin = 0;
      xMax = 1 + Math.max(ideas.map(idea => idea.templated.commentCount.value));
    }
    if (yAxis.id === 'voteCount') {
      yMin = 0;
      yMax = 1 + Math.max(ideas.map(idea => idea.templated.voteCount.value));
    }
    if (yAxis.id === 'commentCount') {
      yMin = 0;
      yMax = 1 + Math.max(ideas.map(idea => idea.templated.commentCount.value));
    }
    yMax = yMax < 10 ? 10 : yMax;
    xMax = xMax < 10 ? 10 : xMax;

    const x = d3.scaleLinear()
      .domain([xMin, xMax])
      .range([0, width]);
    svg.append('g')
      .attr('transform', `translate(0,${height})`)
      .call(d3.axisBottom(x));

    const y = d3.scaleLinear()
      .domain([yMin, yMax])
      .range([height, 0]);

    svg.append('g')
      .call(d3.axisLeft(y));

    const z = d3.scaleLinear()
      .domain([0, 1])
      .range([3, 20]);

    svg.append('text')
      .attr('text-anchor', 'middle')
      .attr('x', width / 2 - 20)
      .attr('y', height + margin.top + 5)
      .text(xAxis.title);

    svg.append('text')
      .attr('text-anchor', ' middle')
      .attr('transform', 'rotate(-90)')
      .attr('y', -margin.left + 20)
      .attr('x', -margin.top - height / 2 + 30)
      .text(yAxis.title);

    const tooltip = d3.select(this.chartRef.current)
      .append('div')
      .style('position', 'absolute')
      .attr('class', 'tooltip')
      .style('background-color', 'black')
      .style('border-radius', '5px')
      .style('padding', '10px')
      .style('color', 'white')
      .style('display', 'none');

    const showTooltip = function (d) {
      tooltip
        .transition()
        .duration(200);
      tooltip
        .style('display', 'unset')
        .html(d.name)
        .style('left', `${d3.mouse(this)[0] + 30}px`)
        .style('top', `${d3.mouse(this)[1] + 140}px`);
    };

    const hideTooltip = function () {
      tooltip
        .transition()
        .duration(200)
        .style('display', 'none');
    };

    const open = function (d) {
      this.setState({ previewId: d._id });
    }.bind(this);

    if (xAxis.id !== null && yAxis.id !== null) {
      svg.append('g')
        .selectAll('dot')
        .data(ideas)
        .enter()
        .append('circle')
        .attr('class', 'bubbles')
        .attr('cx', d => x(xAxis.id in d.templated ? d.templated[xAxis.id].value : xMin))
        .attr('cy', d => y(yAxis.id in d.templated ? d.templated[yAxis.id].value : yMin))
        .attr('r', d => z(d.score ? d.score : 0.1))
        .style('fill', d => (d.isSubmitted ? theme.secondaryColour || '#d15b51' : theme.primaryColour || '#51b3d1'))
        .style('opacity', d => (0.3 + d.score * 2))
        .on('mouseover', showTooltip)
        .style('cursor', 'pointer')
        .on('mouseleave', hideTooltip)
        .on('click', open);
    }
  }

  render() {
    const {
      options, xAxis, yAxis, ideas, total, previewId,
    } = this.state;
    return (
      <Segment>
        <IdeaPreview ideaId={previewId} closeModal={() => this.setState({ previewId: null })} />
        <h3>
          <Icon name="chart bar" />
          Idea analysis
        </h3>
        <p>
          Plot ideas against your chosen criteria. Idea sizes indicate their
          <em>smart score</em>
          , which is
          {util.appName()}
          's automated idea quality metric. Only non-empty ideas are shown below.
        </p>

        <Dropdown
          icon={null}
          style={{ paddingBottom: '10px' }}
          trigger={(
            <Button as="div" labelPosition="left">
              <Label size="tiny" pointing="right">X axis</Label>
              <Button size="tiny" basic icon>
                {xAxis.title}
                <Icon name="dropdown" />
                {' '}

              </Button>
            </Button>
          )}
        >
          <Dropdown.Menu>
            {options && options.map(d => <Dropdown.Item key={d.id} content={d.title} onClick={() => this.setState({ xAxis: d }, this.getChart(ideas, d, this.state.yAxis))} />)}
            <Dropdown.Item key="voteCount" content="Likes" onClick={() => this.setState({ xAxis: { title: 'Likes', id: 'voteCount' } }, this.getChart(ideas, { title: 'Likes', id: 'voteCount' }, this.state.yAxis))} />
            <Dropdown.Item key="commentCount" content="Comments" onClick={() => this.setState({ xAxis: { title: 'Comments', id: 'commentCount' } }, this.getChart(ideas, { title: 'Comments', id: 'commentCount' }, this.state.yAxis))} />
          </Dropdown.Menu>
        </Dropdown>
        <Dropdown
          icon={null}
          trigger={(
            <Button as="div" labelPosition="left">
              <Label size="tiny" pointing="right">Y axis</Label>
              <Button size="tiny" basic icon>
                {yAxis.title}
                <Icon name="dropdown" />
                {' '}

              </Button>
            </Button>
          )}
        >
          <Dropdown.Menu>
            {options && options.map(d => <Dropdown.Item key={d.id} content={d.title} onClick={() => this.setState({ yAxis: d }, this.getChart(ideas, this.state.xAxis, d))} />)}
            <Dropdown.Item key="voteCount" content="Likes" onClick={() => this.setState({ yAxis: { title: 'Likes', id: 'voteCount' } }, this.getChart(ideas, this.state.xAxis, { title: 'Likes', id: 'voteCount' }))} />
            <Dropdown.Item key="commentCount" content="Comments" onClick={() => this.setState({ yAxis: { title: 'Comments', id: 'commentCount' } }, this.getChart(ideas, this.state.xAxis, { title: 'Comments', id: 'commentCount' }))} />
          </Dropdown.Menu>
        </Dropdown>

        <div style={{ marginRight: 20, float: 'right', marginTop: 5 }}>
          <span style={{ display: 'inline', fontSize: 16, marginRight: 4 }}>{' '}</span>
          <Dropdown
            inline
            options={filters}
            value={this.state.filter}
            onChange={(e, c) => this.setState({ filter: c.value }, this.getData)}
          />
          <span>
            (
            {ideas ? ideas.length : 0}
            {' '}
            out of
            {total}
            {' '}
            total)
          </span>
        </div>
        <div style={{ clear: 'both' }} />
        <StyledChart id="chart" ref={this.chartRef} />
      </Segment>
    );
  }
}

export default withTheme(IdeaChart);
